import { useState } from 'react'

import { AlertCircle, AlertTriangle, Building2, ChevronDown, ChevronRight, Users } from 'lucide-react'

import { issueStatusLabel } from '@/shared/constants/issue-status'
import type { IssueSeverity } from '@/shared/constants/issue-status'

import { IssueStatusPill } from './DetailChips'
import { sortSiteIssues } from './SiteDetailParts'
import { useGisIssues } from '../../../hooks/useGisIssues'
import { useResolutionTargets } from '../../../hooks/useResolutionTargets'
import { buildDepartmentWorkload, sortWorkloadRows } from '../../../lib/department-workload'
import { ageDays, isActive } from '../../../lib/issues'

import type { GisIssue, GisScope, GisSite } from '../../../api/types'
import type { LucideIcon } from 'lucide-react'

/** Rows below this show in full; above it the table previews the first `PREVIEW` and offers "View all". */
const PREVIEW = 4

const SEV_ICON: Record<IssueSeverity, LucideIcon> = { critical: AlertTriangle, non_critical: AlertCircle }
const SEV_ICON_COLOR: Record<IssueSeverity, string> = { critical: 'text-gis-red', non_critical: 'text-gis-orange' }

/** Shared column template for the wide table (header + rows stay in lock-step via one inline style). */
const TABLE_COLS = { gridTemplateColumns: 'minmax(0,1fr) 112px 104px 88px 40px' }

/** A flat related-issue row carrying every column the wide table needs + a compact `meta` for narrow. */
interface TableRow {
  id: string
  title: string
  severity: IssueSeverity
  category: string | null
  status: string
  reportedAt: string
  age: number
  meta: string
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** One related-issue row. Wide (`@[520px]/related`) = 5-column table cell; narrow = compact stack. The
 *  whole row is a button → switch the drawer to that issue. */
function Row({ row, onOpen }: { row: TableRow; onOpen: (id: string) => void }) {
  const Icon = SEV_ICON[row.severity]
  return (
    <button
      type="button"
      onClick={() => onOpen(row.id)}
      className="block w-full rounded-[10px] px-2 text-left transition hover:bg-[#f7f9fc] focus-visible:ring-2 focus-visible:ring-blue-500/60"
    >
      {/* Wide: aligned table row */}
      <div className="hidden items-center gap-3 py-[8px] @[520px]/related:grid" style={TABLE_COLS}>
        <div className="flex min-w-0 items-center gap-2">
          <Icon className={`h-[14px] w-[14px] flex-none ${SEV_ICON_COLOR[row.severity]}`} />
          <span className="truncate text-[12px] font-semibold text-gis-ink">{row.title}</span>
        </div>
        <span className="truncate text-[11px] font-medium text-gis-soft">{row.category ?? '—'}</span>
        <span className="min-w-0"><IssueStatusPill status={row.status} /></span>
        <span className="text-[11px] font-medium text-gis-soft tabular-nums">{formatDate(row.reportedAt)}</span>
        <span className="text-right text-[11px] font-semibold text-gis-ink-2 tabular-nums">{row.age}d</span>
      </div>
      {/* Narrow: compact stack */}
      <div className="flex items-center gap-[11px] py-[9px] @[520px]/related:hidden">
        <Icon className={`h-[15px] w-[15px] flex-none ${SEV_ICON_COLOR[row.severity]}`} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12.5px] font-semibold text-gis-ink">{row.title}</div>
          <div className="mt-[3px] truncate text-[11px] font-medium text-gis-soft">{row.meta}</div>
        </div>
        <ChevronRight className="h-4 w-4 flex-none stroke-2 text-gis-muted" />
      </div>
    </button>
  )
}

/** An expandable related-issue section rendered as a responsive table. Expanded by default; `· 0` shows
 *  the count but doesn't expand (the "only issue here" signal). Beyond `PREVIEW` rows, "View all N →"
 *  reveals the rest **in place** — no navigation. */
function RelatedIssueTable({
  icon: LeadIcon,
  label,
  rows,
  onOpen,
}: {
  icon: LucideIcon
  label: string
  rows: TableRow[]
  onOpen: (id: string) => void
}) {
  const [open, setOpen] = useState(true)
  const [showAll, setShowAll] = useState(false)
  const count = rows.length
  const empty = count === 0
  const visible = showAll ? rows : rows.slice(0, PREVIEW)
  const hidden = count - visible.length

  return (
    <div className="@container/related overflow-hidden rounded-[12px] border border-gis-line bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={empty}
        aria-expanded={empty ? undefined : open}
        className="flex w-full items-center gap-2 px-3 py-[11px] text-left transition focus-visible:ring-2 focus-visible:ring-blue-500/60 disabled:cursor-default enabled:hover:bg-[#f7f9fc]"
      >
        <LeadIcon className={`h-4 w-4 flex-none ${empty ? 'text-gis-muted' : 'text-gis-blue'}`} />
        <span className={`flex-1 text-[12px] font-semibold ${empty ? 'text-gis-soft' : 'text-gis-ink'}`}>
          {label} <span className="font-bold text-gis-muted tabular-nums">· {count}</span>
        </span>
        {!empty && (open
          ? <ChevronDown className="h-4 w-4 flex-none stroke-2 text-gis-muted" />
          : <ChevronRight className="h-4 w-4 flex-none stroke-2 text-gis-muted" />)}
      </button>

      {open && !empty && (
        <div className="border-t border-gis-line-2 px-1 pb-1">
          {/* Wide-only column header */}
          <div
            className="hidden items-center gap-3 px-2 pt-[9px] pb-[6px] text-[10px] font-bold tracking-[.05em] text-gis-subheading uppercase @[520px]/related:grid"
            style={TABLE_COLS}
          >
            <span>Issue</span>
            <span>Category</span>
            <span>Status</span>
            <span>Reported</span>
            <span className="text-right">Age</span>
          </div>
          {visible.map((r) => (
            <Row key={r.id} row={r} onOpen={onOpen} />
          ))}
          {hidden > 0 && (
            <div className="px-2 pt-[6px] pb-[8px] text-right">
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="text-[11.5px] font-semibold text-gis-blue transition hover:underline focus-visible:ring-2 focus-visible:ring-blue-500/60"
              >
                View all {count} issues →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export interface RelatedIssuesProps {
  currentId: string
  siteId: string | null
  department: string | null
  scope: GisScope
  sites: GisSite[]
  onOpenIssue: (id: string) => void
}

/**
 * The two in-issue related lists — "Other issues at this site" and "Other issues in {department}" —
 * as responsive tables (columns on a wide panel, compact rows on a narrow one). Both read the cached
 * `useGisIssues(scope)` feed (no new request): the site list filters by siteId; the department list
 * reuses `buildDepartmentWorkload` for ordering, then hydrates each row from the feed for the extra
 * columns. The current issue is excluded from both. Clicking a row calls `onOpenIssue` (switch to it).
 */
export function RelatedIssues({ currentId, siteId, department, scope, sites, onOpenIssue }: RelatedIssuesProps) {
  const { data } = useGisIssues(scope)
  const targets = useResolutionTargets()
  const feed = data?.issues ?? []

  if (!siteId && !department) {
    return null
  }

  const toRow = (i: GisIssue): TableRow => {
    const age = ageDays(i.createdAt)
    return {
      id: i.id,
      title: i.title,
      severity: i.severity,
      category: i.category,
      status: i.status,
      reportedAt: i.createdAt,
      age,
      meta: [i.category, issueStatusLabel(i.status), `${age}d`].filter(Boolean).join(' · '),
    }
  }

  const byId = new Map(feed.map((i) => [i.id, i]))

  const siteRows: TableRow[] = siteId
    ? sortSiteIssues(feed.filter((i) => i.siteId === siteId && i.id !== currentId && isActive(i))).map(toRow)
    : []

  const deptRows: TableRow[] = department
    ? sortWorkloadRows(buildDepartmentWorkload(feed, sites, department, targets).rows.filter((r) => r.id !== currentId), 'aging')
        .map((r) => byId.get(r.id))
        .filter((i): i is GisIssue => i !== undefined)
        .map(toRow)
    : []

  return (
    <div className="space-y-2">
      {siteId && <RelatedIssueTable icon={Building2} label="Other issues at this site" rows={siteRows} onOpen={onOpenIssue} />}
      {department && <RelatedIssueTable icon={Users} label={`Other issues in ${department}`} rows={deptRows} onOpen={onOpenIssue} />}
    </div>
  )
}
